<template>
  <div>
    <!-- Entrée en stock : par article -->
    <div class="modal bg-white fade" tabindex="-1" id="storeEntryItemsModal">
      <div class="modal-dialog modal-fullscreen">
        <form @submit.prevent="submit('item')" class="modal-content shadow-none">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('shop.product_list') }}</h5>
            <div class="d-flex align-items-center gap-8">
              <div class="d-flex align-items-center gap-2">
                <span class="fs-6 fw-bolder text-gray-700 text-nowrap">{{ $t('store.output.create.date') }}</span>
                <input class="form-control form-control-solid fw-bolder w-150px store_entry_date" v-model="form.date" required />
              </div>
              <div class="d-flex align-items-center gap-2">
                <span class="fs-6 fw-bolder text-gray-700 text-nowrap">{{ $t('shop.entries') }} :</span>
                <span class="fw-bolder fs-4">{{ form.numero }}</span>
              </div>
            </div>
            <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" data-bs-dismiss="modal" aria-label="Close">
              <i class="bi bi-x-lg fs-3"></i>
            </div>
          </div>

          <div class="modal-body">
            <div class="card">
              <div class="card-body">
                <div class="row bg-light rounded py-6 px-2 my-2 align-items-end" v-for="(item, index) in form.items" :key="index">
                  <div class="col-md-5 my-2">
                    <label class="required form-label">{{ $t('store.product_placeholder') }}</label>
                    <select class="form-select store-entry-select h-50px" required
                            name="entry_product" :data-key="index" v-model="item.id"
                            data-dropdown-parent="#storeEntryItemsModal" :data-placeholder="$t('store.product_placeholder')">
                      <option></option>
                      <optgroup v-for="group in groupProducts(availableProducts(index))" :key="group.category_id" :label="group.category_name">
                        <option v-for="product in group.products" :key="product.id" :value="product.id">{{ product.declination_title }}</option>
                      </optgroup>
                    </select>
                  </div>
                  <div class="col-md-3 my-2">
                    <label class="form-label">{{ $t('store.entrie.create.lot') }}</label>
                    <select class="form-select store-entry-select h-50px" name="entry_lot" :data-key="index"
                            v-model="item.product_lot_id" data-dropdown-parent="#storeEntryItemsModal"
                            :data-placeholder="$t('store.entrie.create.lot')" data-allow-clear="true">
                      <option></option>
                      <option v-for="lot in lotsOf(item.id)" :key="lot.id" :value="lot.id">{{ lot.lot.name }} ({{ lot.lot.item_number }})</option>
                    </select>
                  </div>
                  <div class="col-md-2 my-2">
                    <label class="required form-label">{{ $t('store.output.create.product_quantity') }}</label>
                    <input required type="number" min="1" v-model="item.quantity" class="form-control h-50px" />
                  </div>
                  <div class="col-md-2 my-2">
                    <button type="button" class="btn btn-light-danger h-50px" :disabled="form.items.length < 2" @click="removeItem(index)">
                      <i class="fa fa-trash"></i>
                    </button>
                  </div>
                </div>
                <button type="button" class="btn btn-sm btn-light-primary mt-4" @click="addItem">
                  <i class="bi bi-plus-lg me-1"></i>{{ $t('product.add_product') }}
                </button>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-light-danger" data-bs-dismiss="modal">{{ $t('button.close') }}</button>
            <button type="submit" class="btn btn-primary" :disabled="isSaving">
              <span v-if="!isSaving">{{ $t('button.save') }}</span>
              <span v-else>{{ $t('loading') }} <span class="spinner-border spinner-border-sm align-middle ms-2"></span></span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Entrée en stock : par catégorie (les articles s'affichent dès la sélection) -->
    <div class="modal bg-white fade" tabindex="-1" id="storeEntryByCategoryModal">
      <div class="modal-dialog modal-fullscreen">
        <form @submit.prevent="submit('category')" class="modal-content shadow-none">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('shop.product_list') }}</h5>
            <div class="d-flex align-items-center gap-8">
              <div class="d-flex align-items-center gap-2">
                <span class="fs-6 fw-bolder text-gray-700 text-nowrap">{{ $t('store.output.create.date') }}</span>
                <input class="form-control form-control-solid fw-bolder w-150px store_entry_date" v-model="form.date" required />
              </div>
              <div class="d-flex align-items-center gap-2">
                <span class="fs-6 fw-bolder text-gray-700 text-nowrap">{{ $t('shop.entries') }} :</span>
                <span class="fw-bolder fs-4">{{ form.numero }}</span>
              </div>
            </div>
            <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" data-bs-dismiss="modal" aria-label="Close">
              <i class="bi bi-x-lg fs-3"></i>
            </div>
          </div>

          <div class="modal-body">
            <div class="row bg-light rounded p-5 mb-5">
              <div class="col-md-12">
                <label class="required form-label">{{ $t('product.category') }}</label>
                <select class="form-select store-entry-select h-50px" name="entry_categories" multiple
                        data-dropdown-parent="#storeEntryByCategoryModal" :data-placeholder="$t('store.entrie.add_by_cat')">
                  <template v-for="category in getCategories" :key="category.id">
                    <template v-if="category.childrens && category.childrens.length">
                      <option disabled>{{ category.name }}</option>
                      <option v-for="sub in category.childrens" :key="sub.id" :value="sub.id">&nbsp;&nbsp;— {{ sub.name }}</option>
                    </template>
                    <option v-else :value="category.id">{{ category.name }}</option>
                  </template>
                </select>
              </div>
            </div>

            <div class="card" v-if="form.items.length">
              <div class="card-body">
                <div class="alert alert-info fw-bold">{{ $t('store.entrie.products_found') }} : {{ form.items.length }}</div>
                <div class="row bg-light rounded py-4 px-2 my-2 align-items-end" v-for="(item, index) in form.items" :key="item.id">
                  <div class="col-md-5 my-2">
                    <label class="form-label fw-bold">{{ item.name }}</label>
                  </div>
                  <div class="col-md-3 my-2">
                    <label class="form-label">{{ $t('store.entrie.create.lot') }}</label>
                    <select class="form-select form-select-solid h-50px" v-model="item.product_lot_id">
                      <option :value="null">—</option>
                      <option v-for="lot in lotsOf(item.id)" :key="lot.id" :value="lot.id">{{ lot.lot.name }} ({{ lot.lot.item_number }})</option>
                    </select>
                  </div>
                  <div class="col-md-2 my-2">
                    <label class="required form-label">{{ $t('store.output.create.product_quantity') }}</label>
                    <input type="number" min="1" v-model="item.quantity" class="form-control h-50px" required />
                  </div>
                  <div class="col-md-2 my-2">
                    <button type="button" class="btn btn-light-danger h-50px" @click="removeItem(index)">
                      <i class="fa fa-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div v-else-if="selectedCategories.length" class="text-muted text-center py-10">{{ $t('store.entrie.no_product_in_category') }}</div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-light-danger" data-bs-dismiss="modal">{{ $t('button.close') }}</button>
            <button type="submit" class="btn btn-primary" :disabled="isSaving || !form.items.length">
              <span v-if="!isSaving">{{ $t('button.save') }}</span>
              <span v-else>{{ $t('loading') }} <span class="spinner-border spinner-border-sm align-middle ms-2"></span></span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Entrée en stock d'un entrepôt, en fenêtres comme pour la boutique :
 * par article (lignes article / lot / quantité) ou par catégorie (les articles
 * des catégories choisies s'affichent directement, il reste les quantités).
 */
import {mapActions, mapGetters} from 'vuex'
import {groupProductsByCategory} from '@/composables/productCategoryGroups'

const today = () => {
  const d = new Date()
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
}

export default {
  name: 'StoreEntryModals',
  props: {
    storeId: {type: String, required: true},
  },
  emits: ['saved'],
  data() {
    return {
      isSaving: false,
      lots: [],
      selectedCategories: [],
      mode: 'item',
      form: {numero: '', date: today(), items: []},
    }
  },
  computed: {
    ...mapGetters('products', ['getProductSaleElements']),
    ...mapGetters('categories', ['getCategories']),
  },
  async mounted() {
    this.loadAllProductSale()
    this.loadCategories()
    this.listProductLots().then(response => { this.lots = response.data ?? [] })
  },
  methods: {
    ...mapActions('products', ['loadAllProductSale', 'listProductLots']),
    ...mapActions('categories', ['loadCategories']),
    ...mapActions('store', ['createEntrieStore', 'showStore']),

    groupProducts(products) {
      return groupProductsByCategory(products, this.getCategories)
    },

    // Un article déjà choisi sans lot n'est plus proposé sur les lignes suivantes
    availableProducts(index) {
      const used = this.form.items
        .filter((item, i) => i !== index && item.id && !item.product_lot_id)
        .map(item => item.id)
      return this.getProductSaleElements.filter(product => !used.includes(product.id))
    },

    lotsOf(productId) {
      return productId ? this.lots.filter(lot => lot.product_sale_element_id === productId) : []
    },

    async open(mode) {
      this.mode = mode
      this.selectedCategories = []
      this.form = {numero: '', date: today(), items: mode === 'item' ? [this.emptyItem()] : []}
      const modalId = mode === 'item' ? '#storeEntryItemsModal' : '#storeEntryByCategoryModal'
      $(modalId).modal('show')
      this.$nextTick(() => {
        this.initPlugins(modalId)
        // réouverture : les listes Select2 gardaient l'ancienne sélection affichée
        $(modalId).find('.store-entry-select').val(null).trigger('change.select2')
      })

      const {data} = await this.showStore(this.storeId)
      const store = data.store
      const prefix = store.prefixe
        ? this.$addYearToPrefixe(store.prefixe)
        : 'ENT/' + store.name.slice(0, 3).toUpperCase() + '/' + new Date().getFullYear()
      this.form.numero = prefix + '/' + this.$incrementValue(data.entryNumber)
    },

    emptyItem() {
      return {id: null, quantity: null, product_lot_id: null}
    },

    addItem() {
      this.form.items.push(this.emptyItem())
      this.$nextTick(() => this.initPlugins('#storeEntryItemsModal'))
    },

    removeItem(index) {
      this.form.items.splice(index, 1)
    },

    // Catégories choisies → leurs articles, en gardant les quantités déjà saisies
    loadCategoryProducts() {
      const previous = Object.fromEntries(this.form.items.map(item => [item.id, item]))
      this.form.items = this.getProductSaleElements
        .filter(product => this.selectedCategories.includes(product.product_category_id))
        .map(product => previous[product.id] ?? {
          id: product.id,
          name: product.declination_title,
          quantity: null,
          product_lot_id: null,
        })
    },

    initPlugins(modalId) {
      const $modal = $(modalId)
      $modal.find('.store_entry_date').flatpickr({dateFormat: 'd/m/Y', onChange: (_, value) => { this.form.date = value }})
      $modal.find('.store-entry-select').each((_, el) => {
        const $el = $(el)
        // reconstruit à chaque ouverture : les options (catégories, articles) arrivent après le premier rendu
        if ($el.data('select2')) {
          $el.off('change.storeEntry').select2('destroy')
        }
        $el.select2({dropdownParent: $modal, placeholder: $el.data('placeholder'), allowClear: el.name === 'entry_lot'})
        $el.on('change.storeEntry', (e) => this.onSelectChange(e))
      })
    },

    onSelectChange(e) {
      const index = e.target.getAttribute('data-key')
      switch (e.target.name) {
        case 'entry_product':
          this.form.items[index].id = e.target.value || null
          this.form.items[index].product_lot_id = null
          break
        case 'entry_lot':
          this.form.items[index].product_lot_id = e.target.value || null
          break
        case 'entry_categories':
          this.selectedCategories = $(e.target).val() || []
          this.loadCategoryProducts()
          break
      }
    },

    async submit(mode) {
      const items = this.form.items
        .filter(item => item.id && Number(item.quantity) > 0)
        .map(item => ({id: item.id, quantity: Number(item.quantity), product_lot_id: item.product_lot_id || null}))
      if (!items.length) return

      this.isSaving = true
      try {
        await this.createEntrieStore({store_id: this.storeId, numero: this.form.numero, date: this.form.date, items})
        window.toastr['success'](i18n.global.t('notification.store.entrie_create'))
        $(mode === 'item' ? '#storeEntryItemsModal' : '#storeEntryByCategoryModal').modal('hide')
        this.$emit('saved')
      } catch (e) {
        window.toastr['error'](e.response?.data?.message ?? i18n.global.t('main.error_try'))
      } finally {
        this.isSaving = false
      }
    },
  },
}
</script>
